/**
 * ResourceFlowWidget - Движение ресурсов по когорте
 * ===================================================
 *
 * Сколько ресурса «в руках» у игроков на каждом уровне кампании / дне жизни:
 * баланс, накопительный приход или расход, отдельно для плательщиков и
 * неплательщиков (динамический статус относительно первой валидированной
 * покупки), с медианой и коридором P25–P75. Опционально — читеры третьей и
 * четвёртой (пунктирной) серией, без коридора.
 *
 * Диапазон дат и платформа — из общих фильтров дашборда; диапазон трактуется
 * как КОГОРТА регистрации (acc_reg_ts), как в ретеншене и прогрессии.
 */

import React, { useState, useMemo } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useFilters } from '../../hooks/useFilters';
import { MetricsService } from '../../services/metricsService';
import { formatDate } from '../../services/filtersService';
import { getPreviousMonthRange } from '../../utils/datePresets';
import { ResourceFlowControls, AXIS_OPTIONS, METRIC_OPTIONS } from './ResourceFlowControls';
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { resources as resourcesSpecs } from '@ggstat/chart-specs';

const DEFAULT_RESOURCE = resourcesSpecs.flow.defaultResource;

// Ряды P50 по ключу серии (nonPayer, payer, …): подпись и цвет — из общего спека
const SERIES = Object.fromEntries(
  resourcesSpecs.flow.series.map((s) => [s.key.replace(/P50$/, ''), s])
);
const NON_PAYER_COLOR = SERIES.nonPayer.color;
const PAYER_COLOR = SERIES.payer.color;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

/** 'YYYY-MM-DD' для локальной даты, без сдвига из-за timezone. */
function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Прошлый полный календарный месяц — тот же дефолт, что у глобального фильтра дат. */
function defaultCohortRange() {
  const { start, end } = getPreviousMonthRange();
  return { from: toIsoDate(start), to: toIsoDate(end) };
}

function resolveCohort(dateRange) {
  const { startDate, endDate } = dateRange || {};
  if (!startDate || !endDate) return defaultCohortRange();
  return { from: formatDate(startDate), to: formatDate(endDate) };
}

const AXIS_LABEL = AXIS_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});
const METRIC_LABEL = METRIC_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});

/**
 * Разворачивает точки ответа сервера в строки для recharts: у каждой серии
 * (payer/nonPayer[/Cheater]) — players/p25/p50/p75 и высота коридора band
 * (p75-p25), которую рисуем как второй, видимый Area со stackId той же серии,
 * поверх невидимого базового Area на уровне p25. Пустая серия (null от
 * сервера) даёт null во всех полях — с connectNulls={false} это разрыв линии,
 * а не проваленный в 0 столбик.
 */
function buildPoints(rawPoints, includeCheaters) {
  const seriesKeys = includeCheaters
    ? ['nonPayer', 'payer', 'nonPayerCheater', 'payerCheater']
    : ['nonPayer', 'payer'];

  return (rawPoints || []).map((row) => {
    const point = { x: row.x };
    seriesKeys.forEach((key) => {
      const s = row[key];
      point[`${key}Players`] = s?.players ?? null;
      point[`${key}P25`] = s ? s.p25 : null;
      point[`${key}P50`] = s ? s.p50 : null;
      point[`${key}P75`] = s ? s.p75 : null;
      point[`${key}Band`] = s ? Math.max(0, s.p75 - s.p25) : null;
    });
    return point;
  });
}

function ResourceFlowTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  const rows = Object.entries(SERIES)
    .map(([key, spec]) => ({ key, label: spec.label, color: spec.color }))
    .filter((row) => point[`${row.key}Players`] != null)
    .sort((a, b) => (point[`${b.key}P50`] ?? -Infinity) - (point[`${a.key}P50`] ?? -Infinity));

  if (rows.length === 0) return null;

  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm space-y-2">
      <p className="font-medium">X: {label}</p>
      {rows.map((row) => (
        <div key={row.key} style={{ color: row.color }}>
          <p className="font-medium">{row.label}</p>
          <p className="text-muted-foreground">
            игроков: {fmtInt(point[`${row.key}Players`])} · p25: {fmtInt(point[`${row.key}P25`])} ·
            {' '}p50: {fmtInt(point[`${row.key}P50`])} · p75: {fmtInt(point[`${row.key}P75`])}
          </p>
        </div>
      ))}
    </div>
  );
}

export function ResourceFlowWidget() {
  const { filters } = useFilters();
  const platform = filters.platform && filters.platform !== 'all' ? filters.platform : undefined;
  // Версии клиента / установки: пустой массив не отправляем, ключ зависимостей — строка
  const clientVersions = filters.clientVersions?.length ? filters.clientVersions : undefined;
  const installVersions = filters.installVersions?.length ? filters.installVersions : undefined;
  const versionsKey = `${clientVersions?.join(',') ?? ''}|${installVersions?.join(',') ?? ''}`;
  // Глобальный диапазон дат дашборда трактуется как когорта регистрации (acc_reg_ts),
  // как в ретеншене и прогрессии. Сервер требует обе границы, поэтому при пустом
  // диапазоне подставляем прошлый календарный месяц.
  const cohort = useMemo(() => resolveCohort(filters.dateRange), [filters.dateRange]);

  const [resource, setResource] = useState(DEFAULT_RESOURCE);
  const [axis, setAxis] = useState('progress');
  const [metric, setMetric] = useState('balance');
  const [includeCheaters, setIncludeCheaters] = useState(false);
  const [showBand, setShowBand] = useState(true);
  const [geo, setGeo] = useState('');

  const namesData = useWidgetData(
    async (_filters, options) => MetricsService.getResourceNames(
      { dateFrom: cohort.from, dateTo: cohort.to, clientVersions, installVersions },
      options
    ),
    [cohort.from, cohort.to, versionsKey]
  );
  const resourceOptions = namesData.data?.names || [];

  const flowData = useWidgetData(
    async (_filters, options) => MetricsService.getResourceFlow(
      {
        resource,
        dateFrom: cohort.from,
        dateTo: cohort.to,
        axis,
        metric,
        includeCheaters: String(includeCheaters),
        platform,
        geo: geo || undefined,
        clientVersions,
        installVersions
      },
      options
    ),
    [resource, axis, metric, includeCheaters, platform, geo, cohort.from, cohort.to, versionsKey]
  );

  const widgetProps = useBaseWidget(flowData);
  const { data } = widgetProps;

  const points = useMemo(
    () => buildPoints(data?.points, includeCheaters),
    [data?.points, includeCheaters]
  );

  const meta = data?.meta;
  const yAxisLabel = METRIC_LABEL[metric] || 'Значение';
  const xAxisLabel = AXIS_LABEL[axis] || 'X';

  const windowClampedTo = meta?.windowClampedTo || namesData.data?.meta?.windowClampedTo || null;

  const metaLine = meta
    ? `Когорта ${cohort.from} — ${windowClampedTo || cohort.to}: ${fmtInt(meta.cohortPlayers)} игроков · читеров: ${fmtInt(meta.cheaterPlayers)} ` +
      `(${includeCheaters ? 'показаны' : 'исключены'}) · ось обрезана на ${fmtInt(meta.truncatedAtX)} ` +
      `· порог корзины ${fmtInt(meta.minPlayers)}`
    : null;

  return (
    <BaseWidget
      {...widgetProps}
      title="Движение ресурсов"
      subtitle="Баланс, приход и расход ресурса по когорте регистрации"
      emptyMessage="Нет данных за выбранную когорту"
      skeletonRows={10}
    >
      <div className="space-y-4">
        <ResourceFlowControls
          resource={resource}
          onResourceChange={setResource}
          resourceOptions={resourceOptions}
          axis={axis}
          onAxisChange={setAxis}
          metric={metric}
          onMetricChange={setMetric}
          includeCheaters={includeCheaters}
          onIncludeCheatersChange={setIncludeCheaters}
          showBand={showBand}
          onShowBandChange={setShowBand}
          geo={geo}
          onGeoChange={setGeo}
        />

        {windowClampedTo && (
          <p className="text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
            Окно обрезано до {windowClampedTo}: данные за сегодня ещё не собраны
          </p>
        )}

        {points.length > 0 && (
          <>
            <ResponsiveContainer width="100%" height={440}>
              <ComposedChart data={points} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  allowDecimals={false}
                  tick={{ fill: 'currentColor' }}
                  className="text-xs"
                  label={{ value: xAxisLabel, position: 'insideBottom', offset: -2 }}
                />
                <YAxis
                  tick={{ fill: 'currentColor' }}
                  className="text-xs"
                  label={{ value: yAxisLabel, angle: -90, position: 'insideLeft' }}
                />
                <Tooltip order="series" content={<ResourceFlowTooltip />} />
                <Legend />

                {showBand && (
                  <>
                    <Area
                      dataKey="nonPayerP25"
                      stackId="nonPayer"
                      stroke="none"
                      fill="transparent"
                      legendType="none"
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                    <Area
                      dataKey="nonPayerBand"
                      stackId="nonPayer"
                      name="Неплательщики P25–P75"
                      stroke="none"
                      fill={NON_PAYER_COLOR}
                      fillOpacity={0.15}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                    <Area
                      dataKey="payerP25"
                      stackId="payer"
                      stroke="none"
                      fill="transparent"
                      legendType="none"
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                    <Area
                      dataKey="payerBand"
                      stackId="payer"
                      name="Плательщики P25–P75"
                      stroke="none"
                      fill={PAYER_COLOR}
                      fillOpacity={0.15}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                  </>
                )}

                <Line
                  type="monotone"
                  dataKey="nonPayerP50"
                  name={SERIES.nonPayer.label}
                  stroke={NON_PAYER_COLOR}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="payerP50"
                  name={SERIES.payer.label}
                  stroke={PAYER_COLOR}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                />

                {includeCheaters && (
                  <>
                    <Line
                      type="monotone"
                      dataKey="nonPayerCheaterP50"
                      name={SERIES.nonPayerCheater.label}
                      stroke={NON_PAYER_COLOR}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="payerCheaterP50"
                      name={SERIES.payerCheater.label}
                      stroke={PAYER_COLOR}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>

            {metaLine && <p className="text-xs text-muted-foreground">{metaLine}</p>}
          </>
        )}

        {points.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Нет данных за выбранную когорту
          </div>
        )}
      </div>
    </BaseWidget>
  );
}

export default ResourceFlowWidget;
